import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PreInvoicesApiAdapter } from '../adapters/PreInvoicesApiAdapter';
import { PreInvoice } from '../models/PreInvoices';

interface UsePreInvoicesProps {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * usePreInvoices - Hook para cargar prefacturas con paginación infinita
 * 
 * Utiliza React Query (useInfiniteQuery) para manejar la carga paginada de prefacturas
 * desde el endpoint /invoices/pre-invoices con soporte para:
 * - Infinite scroll
 * - Filtros avanzados
 * - Sorting
 * - Cache automático
 * 
 * @param {UsePreInvoicesProps} props - Configuración de filtros y sorting
 * @returns {Object} Estado de la query con datos, loading, error y funciones de paginación
 */
export const usePreInvoices = ({
  searchFilter,
  search,
  sortBy = 'id',
  sortDirection = 'desc',
}: UsePreInvoicesProps = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const preInvoicesAdapter = new PreInvoicesApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['preInvoices', searchFilter, search, sortBy, sortDirection],
    queryFn: ({ pageParam = 1 }) =>
      preInvoicesAdapter.getPreInvoicesPaginated({
        searchFilter,
        search,
        sortBy,
        sortDirection,
        page: pageParam,
        limit: 15,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  // Aplanar todas las páginas en un solo array
  const preInvoices: PreInvoice[] =
    data?.pages.flatMap((page) => page.preInvoices) ?? [];

  // Total de resultados (de la primera página)
  const total = data?.pages[0]?.total ?? 0;

  return {
    preInvoices,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
